/**
 * What the app says when something happens.
 *
 * Six screens still called `alert()` — a browser dialog that stops the page,
 * cannot be styled, and has to be dismissed before anything else can happen.
 * One of them put a **new staff member's password** in it, which is the worst
 * place in this app for a secret to appear.
 *
 * A toast says the same thing without taking the screen, and it can carry a
 * button — which is what turns "are you sure?" into "done, undo". A confirm
 * dialog asks a person to be certain before they have seen the result; an undo
 * lets them look first. Undo is the kinder of the two and it is one pattern,
 * applied everywhere, rather than a decision per screen.
 */
import { Toaster as Sonner, toast } from 'sonner';
import { ApiError } from '../api';
import { t } from '../i18n';
import { isRtl } from '../i18n';

export function Toaster() {
  return (
    <Sonner
      position="bottom-center"
      dir={isRtl() ? 'rtl' : 'ltr'}
      // Above the bottom nav, which is 64px plus the home indicator.
      offset={96}
      toastOptions={{
        // His language: squircle, card white, a real border, no shadow theatre.
        classNames: {
          toast:
            'rounded-[1.5rem] border border-border bg-card text-foreground shadow-elev-3 font-sans',
          title: 'text-sm font-bold',
          description: 'text-xs text-muted-foreground',
          actionButton:
            'rounded-full bg-primary px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-primary-foreground',
          error: 'border-urgent/25 bg-urgent/10 text-urgent',
          success: 'border-wealth/25 bg-wealth/10 text-wealth',
        },
      }}
    />
  );
}

/**
 * An error a person can read, from anything that was thrown.
 *
 * Only ApiError messages are safe to show — they are written by the server or
 * by api.ts for exactly this surface. Anything else (a network TypeError's
 * "Load failed", a bug's "Cannot read properties of undefined") is jargon or
 * worse, so those collapse to the fallback.
 */
export function toastError(e: unknown, fallback = t('Failed')): void {
  const message = e instanceof ApiError && e.message ? e.message : fallback;
  toast.error(message);
}

export function toastOk(message: string): void {
  toast.success(message);
}

/**
 * Queued for offline replay, not a failure — see api.ts's isQueuedOffline.
 * Neutral styling on purpose: it did not fail (red) and it has not landed
 * yet either (green would overclaim), it is waiting.
 */
export function toastQueued(message: string): void {
  toast(message);
}

/**
 * Done — and here is the way back.
 *
 * `undo` is called if they press it. The caller decides what undoing means:
 * usually the opposite write, sometimes simply not having sent the first one
 * yet. Five seconds is long enough to notice and short enough not to hover.
 */
export function toastUndo(message: string, undo: () => void): void {
  toast(message, {
    duration: 5000,
    action: { label: t('Undo'), onClick: undo },
  });
}

export { toast };
